import devHours from "../../components/industries/sections/devHours"

/**
 * /industries/field-service data — rebuilt from
 * src/Design/industries/industry-field-service.html, word for word, EXCEPT
 * the two results metrics: the design file's own top comment says "The two
 * results figures are written results, not numbers. Confirm them against
 * the Big Jerry's Fencing case study; use real numbers from it if it has
 * any." It does: src/data/offers.js's field-service-installer-platform.
 * proof.metrics states "40% faster lead-to-installation" and "25% higher
 * quote-to-win rate" verbatim (plus "21 cities across 9 US states", not
 * used here — the layout has room for two) — used below in place of the
 * design's placeholder "1 schedule"/"from the site" figures. Features and
 * FAQ checked against the same offer entry's `included`/`faqs` — both
 * match; nothing else changed. "253k Development hours" uses devHours(),
 * not a snapshot.
 *
 * PerimFlow (stat s4, and the "How does this relate to PerimFlow?" FAQ) is
 * Visiomate's own separate product at perimflow.com — carried from the
 * design as given, an external link, same as the header mega-menu already
 * does.
 *
 * Title tag and meta description are unchanged from the previous version of
 * this page.
 */
const fieldService = ({ heroImage, challengeImages = [], solutionImage, resultsImage }) => ({
  slug: "field-service",
  industryName: "Field Service",

  kicker: "Solutions for businesses with crews in the field",
  headline: "Get your crews off group texts and onto one schedule",
  subheading: "Scheduling, quoting and job tracking your crews can run from their phones.",
  heroImage,
  heroImageAlt: "Field Service work in progress",
  ctaHref: "/contact",
  ctaLabel: "Book your free consultation",
  badge: "Big Jerry's Fencing · installer operations platform",
  stats: [
    { value: "35+", label: "Solutions delivered" },
    { value: `${devHours()}`, label: "Development hours" },
    { value: "30 days", label: "Post-launch warranty" },
    { value: "PerimFlow", label: "Our own field-service product" },
  ],

  challengesEyebrow: "What slows field service teams down",
  challengesHeading: "What keeps field teams on the phone",
  challengesIntro: "The office and the crews are working from different information.",
  challenges: [
    {
      image: challengeImages[0],
      heading: "Crews scheduled by text",
      detail: "Jobs and crews are coordinated over texts and a whiteboard.",
    },
    {
      image: challengeImages[1],
      heading: "Quotes priced by hand",
      detail: "Every quote is worked out manually, so it takes days.",
    },
    {
      image: challengeImages[2],
      heading: "Margin known at invoice",
      detail: "You can't see a job's profit until the invoice goes out.",
    },
    {
      image: challengeImages[3],
      heading: "No clear job status",
      detail: "When a customer asks where their job is, nobody knows for sure.",
    },
  ],

  situationsEyebrow: "How we help field service teams",
  situationsHeading: "Wherever you're starting from",
  situationsIntro: "Field teams come to us with very different setups. These are the four we see most, and how we handle each.",
  situations: [
    {
      tag: "Want it ready to use",
      title: "Get started with PerimFlow",
      detail: "Our own platform for fencing and field-service businesses, ready to run.",
      ghostIconPath:
        '<path d="M5 15c-1 1-1.5 3.5-1.5 5.5 2 0 4.5-.5 5.5-1.5"/><path d="M9 15l-3-3c1.5-4 5-8 12-8 0 7-4 10.5-8 12z"/><circle cx="14.5" cy="9.5" r="1.5"/>',
      footerText: "PerimFlow",
      footerHref: "https://perimflow.com/",
    },
    {
      tag: "Your process is your own",
      title: "Built around how you work",
      detail: "Scheduling, quoting and job status, built around your crews and your jobs.",
      ghostIconPath:
        '<path d="M12 3a2 2 0 0 1 2 2v1h2a2 2 0 0 1 2 2v2h1a2 2 0 0 1 0 4h-1v2a2 2 0 0 1-2 2h-2v1a2 2 0 0 1-4 0v-1H8a2 2 0 0 1-2-2v-2H5a2 2 0 0 1 0-4h1V8a2 2 0 0 1 2-2h2V5a2 2 0 0 1 2-2z"/>',
      footerText: "Installer operations platform",
      footerHref: "/offers/field-service-installer-platform",
    },
    {
      tag: "Missed calls, missed jobs",
      title: "Every call answered",
      detail: "An AI receptionist answers calls, captures the job details and books the visit.",
      ghostIconPath:
        '<path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z"/>',
      footerText: "AI voice receptionist",
      footerHref: "/offers/ai-voice-receptionist",
    },
    {
      tag: "Quotes priced by hand",
      title: "Quotes out faster",
      detail: "We automate quotes from your own rates and templates, so they go out faster and match the job.",
      ghostIconPath: '<path d="M12 3v4M12 17v4M3 12h4M17 12h4M6 6l2.5 2.5M15.5 15.5 18 18M6 18l2.5-2.5M15.5 8.5 18 6"/>',
      footerText: "Document automation",
      footerHref: "/offers/ai-quote-and-document-automation",
    },
  ],
  situationsUnsureHeading: "Not sure which fits?",
  situationsUnsureDetail: "The Fit Sprint gives you a costed plan before you commit to anything.",
  situationsUnsureLinkLabel: "See the Fit Sprint",
  situationsUnsureLinkHref: "/offers/ops-and-erp-fit-sprint",

  resultsEyebrow: "Results for field service clients",
  resultsHeading: "From group texts to one schedule",
  // Real numbers from the case study (see file-top note), not the design's
  // written placeholders.
  resultsMetrics: [
    { value: "40%", label: "faster lead-to-installation" },
    { value: "25%", label: "higher quote-to-win rate" },
  ],
  resultsSummary: "Scheduling, quoting and job tracking now run from one platform the office and crews both use.",
  resultsLinkLabel: "Read the case study",
  resultsLinkHref: "/caseStudies/projects/bigJerrysFencing",
  resultsImage,
  resultsImageAlt: "Big Jerry's Fencing",

  // Names resolved against src/data/clientLogos.js by TrustedBy.js.
  trustedByLogos: ["Big Jerry's Fencing", "ORR Protection"],

  whyEyebrow: "Why Visiomate for field service",
  whyHeading: "What you get with this team",
  whyIntro: "Three things that stay the same on every engagement.",
  whyLinkHref: "/contact",
  reasons: [
    {
      title: "Same team, start to finish",
      detail: "The people who start your project finish it, with the founder involved throughout.",
    },
    { title: "30-day warranty", detail: "Everything we configure or build is covered for 30 days after go-live." },
    {
      title: "We pick what fits your setup",
      detail: "Odoo, Dynamics 365 or a custom build — we recommend what suits your business, not one product.",
    },
  ],

  questionsEyebrow: "Questions",
  questionsHeading: "Questions buyers ask before they commit",
  questionsIntro: "Something else on your mind?",
  questionsLinkHref: "/contact",
  faqs: [
    {
      q: "We're a small field-service business — is this overkill?",
      a: "No. It's set up around the crews and job types you have now, and grows as you add more.",
    },
    { q: "Can crews use this from their phones in the field?", a: "Yes. Crews see their schedule and update job status from their phones." },
    {
      q: "What's the difference between PerimFlow and a custom platform?",
      a: "PerimFlow is our ready-made product for fencing and field-service businesses. A custom platform is built around your own process. We'll tell you which fits.",
    },
    {
      q: "How do we start if we're not sure what we need?",
      a: "With the Fit Sprint: we map how you work and give you a costed plan before you commit to anything.",
    },
  ],

  ctaHeading: "Start with a costed roadmap, not a proposal",
  ctaDetail: "The Ops & ERP Fit Sprint gives you a costed plan before you commit to anything larger.",
  ctaLinkLabel: "See the Fit Sprint",
  ctaLinkHref: "/offers/ops-and-erp-fit-sprint",

  metaTitle: "Field Service Software Development Company | Visiomate",
  metaDescription:
    "A field-service platform for installers: quoting, dispatch, a crew mobile app and a customer portal, live in four to eight weeks.",
})

export default fieldService
